import { z } from 'zod'

const trimUrl = (s: string) => s.trim()

export const PUBLIC_MARKETING_SITE_URL = 'https://brushfireinsights.com'
export const LOCAL_MARKETING_SITE_URL = 'http://localhost:3000'

export function resolveMarketingSiteUrl(raw: string | undefined, isDev: boolean): string {
  const trimmed = (raw ?? '').trim().replace(/\/$/, '')
  if (trimmed) {
    return trimmed
  }
  return isDev ? LOCAL_MARKETING_SITE_URL : PUBLIC_MARKETING_SITE_URL
}

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().default('').transform(trimUrl),
  VITE_APP_NAME: z.string().default('Brushfire Signal Provider'),
  /**
   * Optional Socket.io base URL for bell notifications.
   * Leave empty in local dev — client uses SPA origin + Vite `/socket.io` proxy → :4005.
   */
  VITE_NOTIFICATION_SERVICE_WS_URL: z.string().default('').transform(trimUrl),
  VITE_COPYRIGHT_TEXT: z
    .string()
    .default(`© ${new Date().getFullYear()} Brushfire Inc. All rights reserved.`),
  /**
   * Minimum USD balance to access Rewards / redeem (BFP-300). Must match platform SP_MIN_REDEEM_USD.
   * Set to 0 to disable the platform minimum (brand minimums still apply).
   */
  VITE_MIN_REDEEM_USD: z
    .string()
    .default('5')
    .transform((s) => Number.parseFloat(s))
    .pipe(z.number().nonnegative()),
  /**
   * Google Play Store listing URL for the Brushfire mobile app.
   * Leave empty to disable the Play Store button.
   */
  VITE_GOOGLE_PLAY_URL: z.string().default('').transform(trimUrl),
  /**
   * Apple App Store listing URL for the Brushfire mobile app.
   * Leave empty to disable the App Store button.
   */
  VITE_APP_STORE_URL: z.string().default('').transform(trimUrl),
  /** Cloudflare Turnstile site key for public delete-account page (optional). */
  VITE_CLOUDFLARE_TURNSTILE_SITE_KEY: z.string().default('').transform(trimUrl),
  /** PostHog project API key (public client key). Leave empty to disable analytics. */
  VITE_POSTHOG_KEY: z.string().default('').transform(trimUrl),
  /** PostHog ingestion host — Cloud US or local hobby stack. */
  VITE_POSTHOG_HOST: z
    .string()
    .default('https://us.i.posthog.com')
    .transform(trimUrl),
  /**
   * Deployment environment tag registered as PostHog super-property (BFP-492).
   * Defaults to `local` in dev builds and `prod` in production builds.
   */
  VITE_APP_ENV: z
    .string()
    .default('')
    .transform((s) => {
      const trimmed = trimUrl(s)
      const allowed = ['local', 'dev', 'qa', 'staging', 'prod'] as const
      if ((allowed as readonly string[]).includes(trimmed)) {
        return trimmed as (typeof allowed)[number]
      }
      return import.meta.env.DEV ? 'local' : 'prod'
    }),
  /**
   * Last-resort UI fallback until identity `/me` loads.
   * Source of truth is Mongo `web_survey_registration_type_enabled` (default true).
   */
  VITE_WEB_SURVEY_REGISTRATION_TYPE_ENABLED: z
    .string()
    .optional()
    .default('true')
    .transform((s) => String(s).trim().toLowerCase() === 'true'),
  /**
   * Public marketing site origin — Terms (`/terms`) and Privacy (`/privacy`) open here.
   * Local Vite defaults to localhost; production/QA/staging builds use the public
   * marketing site unless this is set explicitly.
   */
  VITE_MARKETING_SITE_URL: z
    .string()
    .optional()
    .transform((s) => resolveMarketingSiteUrl(s, import.meta.env.DEV)),
})

export const env = envSchema.parse(import.meta.env)
